import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Truck, RotateCcw, Calendar, Hash, FileText, UserCheck, Navigation, Phone } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import { api } from '@/shared/api/axios';
import { useSampleMutations } from '../api';
import type { SampleRequest } from '../types';

interface SampleDispatchModalProps {
  open: boolean;
  onClose: () => void;
  sample: SampleRequest | null;
  isRedispatch?: boolean;
}

interface FleetDriver {
  _id: string;
  userId: { _id: string; username: string; display_name: string } | string;
  name: string;
  phone: string;
  vehicleNumber: string;
  vehicleType: string;
  capacityDrums: number;
  status: 'available' | 'on_trip' | 'off_duty';
}

/** YYYY-MM-DD, `days` after `from` (today when blank). */
function dayAfter(from: string, days: number) {
  const d = from ? new Date(`${from}T00:00:00`) : new Date();
  d.setDate(d.getDate() + days);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const COURIER_PRESETS = [
  'Blue Dart Express',
  'DTDC Courier',
  'Delhivery Logistics',
  'Professional Courier',
  'Safechem Logistics (Chemical Cargo)',
  'Trackon Courier',
  'Shree Maruti Courier',
  'Direct Field Delivery/BDE Handover',
  'Other',
];

export function SampleDispatchModal({
  open,
  onClose,
  sample,
  isRedispatch = false,
}: SampleDispatchModalProps) {
  const { dispatchSample, redispatchSample } = useSampleMutations();

  // Courier first (client, 2026-10-30: "in-house drivers abhi nahi hai, use
  // nahi kar rahe"). The fleet list is only loaded if someone picks it — it
  // also needs Inventory access, which sales reps do not have.
  const [dispatchMode, setDispatchMode] = useState<'fleet' | 'courier'>('courier');
  const [drivers, setDrivers] = useState<FleetDriver[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [loadingDrivers, setLoadingDrivers] = useState<boolean>(false);
  const [driversLoaded, setDriversLoaded] = useState(false);

  const [courier, setCourier] = useState('');
  const [customCourier, setCustomCourier] = useState('');
  const [awb, setAwb] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [notes, setNotes] = useState('');
  // The follow-up the Sample Invoice must carry (client, 2026-10-30) — by
  // default three days after the expected delivery, until the user picks one.
  const [followupDate, setFollowupDate] = useState(dayAfter('', 5));
  const [followupTouched, setFollowupTouched] = useState(false);
  const [followupNote, setFollowupNote] = useState('');

  useEffect(() => {
    if (open) { setFollowupDate(dayAfter('', 5)); setFollowupTouched(false); setFollowupNote(''); setDispatchMode('courier'); }
  }, [open]);

  useEffect(() => {
    if (!followupTouched) setFollowupDate(dayAfter(expectedDelivery, expectedDelivery ? 3 : 5));
  }, [expectedDelivery, followupTouched]);

  useEffect(() => {
    if (open && dispatchMode === 'fleet' && !driversLoaded) {
      setLoadingDrivers(true);
      setDriversLoaded(true);
      api.get<{ ok: boolean; drivers: FleetDriver[] }>('/api/fleet/drivers')
        .then((res) => {
          if (res?.data?.drivers) {
            setDrivers(res.data.drivers);
            const firstAvail = res.data.drivers.find((d) => d.status === 'available') || res.data.drivers[0];
            if (firstAvail) {
              const uId = typeof firstAvail.userId === 'object' ? firstAvail.userId._id : firstAvail.userId;
              setSelectedDriverId(String(uId || firstAvail._id));
            }
          }
        })
        .catch(() => {})
        .finally(() => setLoadingDrivers(false));
    }
  }, [open, dispatchMode, driversLoaded]);

  if (!sample) return null;

  const selectedDriver = drivers.find((d) => {
    const uId = typeof d.userId === 'object' ? d.userId._id : d.userId;
    return String(uId) === selectedDriverId || String(d._id) === selectedDriverId;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (dispatchMode === 'fleet') {
      if (!selectedDriver) {
        toast.error('Please select an in-house fleet driver.');
        return;
      }
    } else {
      if (!courier.trim() || (courier === 'Other' && !customCourier.trim())) {
        toast.error('Please specify the courier or transporter.');
        return;
      }
    }

    if (!isRedispatch) {
      if (!followupDate) {
        toast.error('Set the follow-up date — when to ask the customer about this sample.');
        return;
      }
      if (followupDate < dayAfter('', 0)) {
        toast.error('The sample follow-up cannot be in the past.');
        return;
      }
    }

    const driverUserId = selectedDriver
      ? (typeof selectedDriver.userId === 'object' ? selectedDriver.userId._id : selectedDriver.userId)
      : null;

    const payload = {
      dispatchMode,
      courier: dispatchMode === 'fleet' ? `RBM Fleet (${selectedDriver?.vehicleNumber})` : (courier === 'Other' ? customCourier.trim() : courier.trim()),
      awb: dispatchMode === 'fleet' ? `FLT-${Date.now().toString().slice(-6)}` : awb.trim(),
      driverId: dispatchMode === 'fleet' ? driverUserId : undefined,
      driverName: dispatchMode === 'fleet' ? selectedDriver?.name : undefined,
      driverPhone: dispatchMode === 'fleet' ? selectedDriver?.phone : undefined,
      vehicleNumber: dispatchMode === 'fleet' ? selectedDriver?.vehicleNumber : undefined,
      destinationLat: 18.6279, // Pune Industrial zone
      destinationLng: 73.8131,
      expectedDelivery: expectedDelivery || undefined,
      notes: notes.trim(),
      ...(isRedispatch ? {} : { followup: { date: new Date(`${followupDate}T11:00:00`).toISOString(), note: followupNote.trim() } }),
    };

    if (isRedispatch) {
      redispatchSample.mutate(
        { id: sample._id, payload },
        {
          onSuccess: () => {
            toast.success(`Revised sample ${sample.code} dispatched via ${dispatchMode === 'fleet' ? 'Fleet Driver' : 'Courier'}.`);
            onClose();
          },
          onError: (err: any) => {
            toast.error(err?.response?.data?.error || 'Failed to redispatch sample.');
          },
        },
      );
    } else {
      dispatchSample.mutate(
        { id: sample._id, payload },
        {
          onSuccess: (updated) => {
            toast.success(dispatchMode === 'fleet'
              ? `Sample ${sample.code} sent to Dispatch — driver assigned.`
              : `Sample ${sample.code} sent to Dispatch.`);
            // deliveryCode only comes back for a fleet dispatch (the QR
            // delivery-confirmation code) — surface it when the automatic
            // WhatsApp send failed (session not linked, bad number, ...) so
            // staff can still share it with the customer by hand.
            if (updated.deliveryCode && !updated.qrSent) {
              toast(
                `WhatsApp send failed — share this delivery code with the customer yourself: ${updated.deliveryCode}`,
                { icon: '⚠️', duration: 10000 },
              );
            } else if (updated.deliveryCode) {
              toast.success(`Delivery QR sent to the customer on WhatsApp (code ${updated.deliveryCode}).`, { duration: 6000 });
            }

            // THE PAPER THAT TRAVELS WITH THE GOODS.
            //
            // Raised server-side in the same request that stamped the driver
            // and took the stock out, so it exists the instant this returns —
            // the driver has not left yet, and printing it is the next thing
            // the dispatcher does. Opened rather than merely announced,
            // because a link in a toast that vanishes in four seconds is a
            // sample going out with no document.
            const invoice = (updated as { sampleInvoice?: { _id: string; invoiceNumber: string } }).sampleInvoice;
            if (invoice?._id) {
              window.open(`/api/documents/sample-invoice/${invoice._id}`, '_blank', 'noopener,noreferrer');
              toast.success(`Sample Invoice ${invoice.invoiceNumber} raised — print it and send it with the goods.`, { duration: 8000 });
            } else {
              toast(
                'Dispatched, but the Sample Invoice could not be raised. Do not send the goods without paperwork — check the server log.',
                { icon: '⚠️', duration: 12000 },
              );
            }
            const costWarning = (updated as { costWarning?: string }).costWarning;
            if (costWarning) toast(costWarning, { icon: '⚠️', duration: 8000 });
            onClose();
          },
          onError: (err: any) => {
            toast.error(err?.response?.data?.error || 'Failed to dispatch sample.');
          },
        },
      );
    }
  };

  const isSubmitting = dispatchSample.isPending || redispatchSample.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        isRedispatch
          ? `Re-dispatch Revised Sample: ${sample.code}`
          : `Dispatch Sample Request: ${sample.code}`
      }
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Recipient Card */}
        <div className="p-3 rounded-lg bg-success/10/80 border border-success/30 flex items-start gap-2.5">
          {isRedispatch ? (
            <RotateCcw className="w-4 h-4 text-success-text mt-0.5" />
          ) : (
            <Truck className="w-4 h-4 text-success-text mt-0.5" />
          )}
          <div className="text-xs text-success-text flex-1">
            <div className="flex items-center justify-between">
              <span className="font-bold">Recipient: {sample.customerName}</span>
              {sample.phone && <span className="text-success-text font-mono text-data-xs"><Phone className="w-3 h-3 inline mr-0.5" />{sample.phone}</span>}
            </div>
            {sample.shippingAddress && (
              <p className="text-success-text text-data-xs mt-0.5 line-clamp-2">
                📍 {sample.shippingAddress}
              </p>
            )}
          </div>
        </div>

        {/* Dispatch Channel Toggle */}
        <div>
          <label className="block text-xs font-semibold text-on-surface-variant mb-1.5">
            Dispatch Method / Channel <span className="text-destructive">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2 p-1 bg-muted rounded-lg border border-border">
            <button
              type="button"
              onClick={() => setDispatchMode('courier')}
              className={`py-2 px-3 rounded-md text-xs font-medium flex items-center justify-center gap-1.5 transition ${
                dispatchMode === 'courier'
                  ? 'bg-success text-white shadow-sm font-semibold'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              <FileText className="w-3.5 h-3.5" /> 📦 Courier / Transporter
            </button>
            <button
              type="button"
              onClick={() => setDispatchMode('fleet')}
              className={`py-2 px-3 rounded-md text-xs font-medium flex items-center justify-center gap-1.5 transition ${
                dispatchMode === 'fleet'
                  ? 'bg-success text-white shadow-sm font-semibold'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              <Truck className="w-3.5 h-3.5" /> 🚚 In-House Driver
            </button>
          </div>
        </div>

        {dispatchMode === 'fleet' ? (
          <div className="space-y-3 p-3 bg-success/10/50 rounded-lg border border-success/30">
            <div>
              <label className="block text-xs font-semibold text-on-surface-variant mb-1 flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <UserCheck className="w-3.5 h-3.5 text-success-text" /> Select Truck Driver & Vehicle
                </span>
                {loadingDrivers && <span className="text-data-xs text-on-surface-variant">Loading fleet...</span>}
              </label>
              {!loadingDrivers && drivers.length === 0 && (
                <p className="mb-2 rounded-md bg-warning/10 px-2.5 py-2 text-xs text-warning-text">
                  No in-house drivers are set up. Use <button type="button" className="font-semibold underline" onClick={() => setDispatchMode('courier')}>Courier / Transporter</button> — pick "Direct Field Delivery/BDE Handover" if you are taking it yourself.
                </p>
              )}

              <select
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-border text-xs bg-card focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                required
              >
                {drivers.map((d) => {
                  const uId = typeof d.userId === 'object' ? d.userId._id : d.userId;
                  const key = String(uId || d._id);
                  return (
                    <option key={key} value={key}>
                      {d.name} — {d.vehicleNumber} ({d.vehicleType || 'Truck'}) — [{d.status.toUpperCase()}]
                    </option>
                  );
                })}
              </select>
            </div>

            {selectedDriver && (
              <div className="p-2.5 bg-card rounded-md border border-success/30 text-xs grid grid-cols-2 gap-2 text-on-surface-variant">
                <div>
                  <span className="text-on-surface-variant text-data-xs">Assigned Driver:</span>
                  <p className="font-semibold text-on-surface">{selectedDriver.name}</p>
                  <p className="text-data-xs text-on-surface-variant">📞 {selectedDriver.phone}</p>
                </div>
                <div>
                  <span className="text-on-surface-variant text-data-xs">Vehicle / Capacity:</span>
                  <p className="font-semibold text-success-text font-mono">{selectedDriver.vehicleNumber}</p>
                  <p className="text-data-xs text-on-surface-variant">{selectedDriver.vehicleType} ({selectedDriver.capacityDrums || 7} Drum)</p>
                </div>
                <div className="col-span-2 pt-1 border-t border-border flex items-center gap-1 text-data-xs text-success-text">
                  <Navigation className="w-3 h-3" />
                  <span>GPS Destination route automatically connected to Live Fleet Radar.</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Courier / Transporter */}
            <div>
              <label className="block text-xs font-semibold text-on-surface-variant mb-1 flex items-center gap-1">
                <Truck className="w-3.5 h-3.5 text-on-surface-variant" /> Courier / Dispatch Method{' '}
                <span className="text-destructive">*</span>
              </label>
              <select
                value={courier}
                onChange={(e) => setCourier(e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-border text-xs bg-card focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                required
              >
                <option value="" disabled>Select courier name</option>
                {COURIER_PRESETS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {courier === 'Other' && (
                <div className="mt-2">
                  <Input
                    value={customCourier}
                    onChange={(e) => setCustomCourier(e.target.value)}
                    placeholder="Enter custom courier name"
                    className="text-xs"
                    required
                  />
                </div>
              )}
            </div>

            {/* AWB Tracking */}
            <div>
              <label className="block text-xs font-semibold text-on-surface-variant mb-1 flex items-center gap-1">
                <Hash className="w-3.5 h-3.5 text-on-surface-variant" /> AWB / Docket / Tracking No.
              </label>
              <Input
                value={awb}
                onChange={(e) => setAwb(e.target.value)}
                placeholder="e.g. BD123456789IN"
                className="font-mono text-xs"
              />
            </div>
          </>
        )}

        {/* Expected Delivery Date */}
        <div>
          <label className="block text-xs font-semibold text-on-surface-variant mb-1 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-on-surface-variant" /> Expected Delivery Date
          </label>
          <Input
            type="date"
            value={expectedDelivery}
            onChange={(e) => setExpectedDelivery(e.target.value)}
            className="text-xs"
          />
        </div>

        {/* Follow-up — mandatory with the Sample Invoice */}
        {!isRedispatch && (
          <div className="rounded-lg border border-primary/25 bg-primary/5 p-3">
            <label className="block text-xs font-semibold text-on-surface mb-1.5 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5 text-primary" /> Follow up with the customer on <span className="text-danger-text">*</span>
            </label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input
                type="date"
                value={followupDate}
                min={dayAfter('', 0)}
                onChange={(e) => { setFollowupDate(e.target.value); setFollowupTouched(true); }}
                className="text-xs"
              />
              <Input
                value={followupNote}
                onChange={(e) => setFollowupNote(e.target.value)}
                placeholder="What to ask (optional)"
                className="text-xs"
              />
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-xs font-semibold text-on-surface-variant mb-1 flex items-center gap-1">
            <FileText className="w-3.5 h-3.5 text-on-surface-variant" /> Dispatch Instructions / Cargo Notes
          </label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., 7 Drums 210L cutting oil with test certificates attached"
            className="w-full p-2.5 rounded-lg border border-border text-xs bg-card focus:ring-2 focus:ring-emerald-600 focus:outline-none resize-none"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" size="sm" className="bg-success hover:bg-success/90 text-white" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="flex items-center gap-1">
                <Spinner size="sm" /> Dispatching...
              </span>
            ) : isRedispatch ? (
              'Confirm Re-dispatch'
            ) : (
              dispatchMode === 'fleet' ? 'Confirm Dispatch & Assign Driver' : 'Confirm Dispatch'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
